import { useMemo, useState, useRef, useEffect } from "react";
import type React from "react";
import type { PlanEvent } from "../../../../shared/types";
import {
  useCategories,
  getCategoryBarColor,
  setWorkHoursTitle,
} from "../../../../shared/stores/category-store";
import { getWeekDays, getToday } from "../../../../shared/types/date-helpers";
import { expandRecurringEvents } from "../../../events/utils/expand-recurring";
import { timeToMinutes } from "../../../calendar/utils/time-positioning";

interface WorkHoursStatsProps {
  readonly events: PlanEvent[];
}

export function WorkHoursStats({ events }: WorkHoursStatsProps) {
  const { categories, labels, categoryOptions, workHoursTitle } = useCategories();

  const weekHours = useMemo(() => {
    const today = getToday();
    const weekDays = getWeekDays(today);
    const weekStart = weekDays[0]!;
    const weekEnd = weekDays[6]!;
    const expanded = expandRecurringEvents(events, weekStart, weekEnd);

    const hours: Record<string, number> = {};
    const noHidden: Record<string, number> = {};
    for (const cat of categories) {
      hours[cat] = 0;
      noHidden[cat] = 0;
    }

    for (const event of expanded) {
      if (event.allDay) continue;
      const duration =
        (timeToMinutes(event.endTime) - timeToMinutes(event.startTime)) / 60;
      if (duration <= 0) continue;
      hours[event.category] = (hours[event.category] ?? 0) + duration;
      if (!event.tags.includes("hidden")) {
        noHidden[event.category] = (noHidden[event.category] ?? 0) + duration;
      }
    }
    return { hours, noHidden };
  }, [events, categories]);

  const rows = useMemo(() => {
    const result: Array<{
      key: string;
      label: string;
      hours: number;
      color: string;
      goalHours?: number;
      extra?: React.ReactNode;
    }> = [];

    for (const cat of categories) {
      const opts = categoryOptions[cat];
      if (opts && !opts.showInWorkHours) continue;

      const catHours = weekHours.hours[cat] ?? 0;
      const catNoHidden = weekHours.noHidden[cat] ?? 0;
      const hasHidden = catHours !== catNoHidden;
      const goalHours = opts?.goalHours ?? null;

      const goalExtra = goalHours
        ? (hours: number) => {
            const pct = Math.min(Math.round((hours / goalHours) * 100), 100);
            return (
              <span className="text-xs text-ink-faint ml-1">
                / {goalHours}h ({pct}%)
              </span>
            );
          }
        : undefined;

      if (hasHidden) {
        result.push({
          key: `${cat}-full`,
          label: `${labels[cat]} (complet)`,
          hours: catHours,
          color: getCategoryBarColor(cat),
          goalHours: goalHours ?? undefined,
          extra: goalExtra?.(catHours),
        });
        result.push({
          key: `${cat}-visible`,
          label: `${labels[cat]} (sans cachés)`,
          hours: catNoHidden,
          color: getCategoryBarColor(cat),
        });
      } else {
        result.push({
          key: cat,
          label: labels[cat],
          hours: catHours,
          color: getCategoryBarColor(cat),
          goalHours: goalHours ?? undefined,
          extra: goalExtra?.(catHours),
        });
      }
    }
    return result;
  }, [categories, labels, categoryOptions, weekHours]);

  const maxHours = Math.max(...rows.map((r) => r.hours), 1);

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(workHoursTitle);
  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingTitle) titleInputRef.current?.focus();
  }, [editingTitle]);

  const handleSaveTitle = () => {
    const trimmed = titleDraft.trim();
    if (trimmed) setWorkHoursTitle(trimmed);
    else setTitleDraft(workHoursTitle);
    setEditingTitle(false);
  };

  return (
    <div className="bg-surface border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border/50">
        {editingTitle ? (
          <input
            ref={titleInputRef}
            type="text"
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={handleSaveTitle}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSaveTitle();
              if (e.key === "Escape") { setTitleDraft(workHoursTitle); setEditingTitle(false); }
            }}
            className="text-sm font-bold text-ink bg-transparent border-b border-accent focus:outline-none"
            placeholder="Titre de la section"
          />
        ) : (
          <button
            type="button"
            onClick={() => { setTitleDraft(workHoursTitle); setEditingTitle(true); }}
            className="text-sm font-bold text-ink hover:text-accent transition-colors flex items-center gap-1.5"
            title="Cliquer pour modifier le titre"
          >
            {workHoursTitle}
            <svg className="w-3 h-3 text-ink-faint opacity-0 group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
        )}
        {/* Total hebdomadaire */}
        <div className="text-right">
          <div className="text-2xl font-black text-ink">
            {Object.values(weekHours.hours).reduce((a, b) => a + b, 0).toFixed(0)}h
          </div>
          <div className="text-[10px] text-ink-faint uppercase tracking-wide">
            Cette semaine
          </div>
        </div>
      </div>

      {/* Barres de progression par catégorie */}
      <div className="p-5 space-y-3">
        {rows.map(({ key, label, hours, color, goalHours }) => {
          const barMax = goalHours ?? maxHours;
          const barPct = Math.min((hours / barMax) * 100, 100);
          const goalReached = goalHours && hours >= goalHours;
          return (
            <div key={key} className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-ink">{label}</span>
                <span className="text-sm font-semibold text-ink">
                  {hours.toFixed(1)}h
                  {goalHours && (
                    <span className={`ml-1 text-xs ${goalReached ? "text-success" : "text-ink-faint"}`}>
                      / {goalHours}h
                    </span>
                  )}
                </span>
              </div>
              <div className="w-full h-3 bg-surface-alt rounded-full overflow-hidden shadow-inner">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${color}`}
                  style={{ width: `${barPct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
